import apiClient from '../../../common/services/apiClient';
import { createQueryString, removeFalsyProperties } from '../../../common/utils/helpers';

export const apiCallIds = {
  // Institutions & requisitions
  FETCH_INSTITUTIONS: 'PSD2/FETCH_INSTITUTIONS',
  CREATE_REQUISITION: 'PSD2/CREATE_REQUISITION',
  GET_REQUISITION: 'PSD2/GET_REQUISITION',

  // Account data
  GET_IBANS: 'PSD2/GET_IBANS',
  GET_ACCOUNT_DATA: 'PSD2/GET_ACCOUNT_DATA',
  GET_ACCOUNT_BALANCES: 'PSD2/GET_ACCOUNT_BALANCES',

  // Transactions
  GET_ACCOUNT_TX_MONTH: 'PSD2/GET_ACCOUNT_TX_MONTH',
  GET_ACCOUNT_TX_ALL: 'PSD2/GET_ACCOUNT_TX_ALL',

  // Categories
  FETCH_CATEGORIES: 'PSD2/FETCH_CATEGORIES',
  CREATE_CATEGORY: 'PSD2/CREATE_CATEGORY',
  UPDATE_CATEGORY: 'PSD2/UPDATE_CATEGORY',
  DELETE_CATEGORY: 'PSD2/DELETE_CATEGORY',

  // Aggregation criteria
  FETCH_AGGREGATION_CRITERIA: 'PSD2/FETCH_AGGREGATION_CRITERIA',
  CREATE_AGGREGATION_CRITERIA: 'PSD2/CREATE_AGGREGATION_CRITERIA',
  UPDATE_AGGREGATION_CRITERIA: 'PSD2/UPDATE_AGGREGATION_CRITERIA',
  DELETE_AGGREGATION_CRITERIA: 'PSD2/DELETE_AGGREGATION_CRITERIA',

  // Loan imported accounts
  GET_LOAN_IMPORTED_ACCOUNTS: 'PSD2/GET_LOAN_IMPORTED_ACCOUNTS',

  // Purge
  PURGE_ACCOUNT_DATA: 'PSD2/PURGE_ACCOUNT_DATA',
};

export default {
  /**
   * Institutions
   * GET /api/psd2/institutions
   */
  fetchInstitutions: (options = {}) => {
    const qs = createQueryString(removeFalsyProperties(options)); // supports ?search=
    return apiClient.get(`/psd2/institutions${qs ? `?${qs}` : ''}`, {
      apiCallId: apiCallIds.FETCH_INSTITUTIONS,
    });
  },

  /**
   * Requisitions
   * POST /api/psd2/requisitions
   * body: { institutionId, loanId, redirectUrl }
   */
  createRequisition: ({ institutionId, loanId, redirectUrl }) => {
    const body = removeFalsyProperties({ institutionId, loanId, redirectUrl });
    return apiClient.post(`/psd2/requisitions`, body, {
      apiCallId: apiCallIds.CREATE_REQUISITION,
    });
  },

  /**
   * GET /api/psd2/requisitions/:requisitionId
   */
  getRequisition: (requisitionId) => {
    return apiClient.get(`/psd2/requisitions/${requisitionId}`, {
      apiCallId: apiCallIds.GET_REQUISITION,
    });
  },

  /**
   * Account data & balances
   * POST /api/psd2/iban
   * GET /api/psd2/account-data/:id
   * GET /api/psd2/account-balances/:id
   */
  getAccountIbans:(ids) => {
    return apiClient.post('/psd2/iban',{ids},{apiCallId: apiCallIds.GET_IBANS})
  },

  getAccountData: (id) => {
    return apiClient.get(`/psd2/account-data/${id}`, {
      apiCallId: apiCallIds.GET_ACCOUNT_DATA,
    });
  },

  getAccountBalances: (id) => {
    return apiClient.get(`/psd2/account-balances/${id}`, {
      apiCallId: apiCallIds.GET_ACCOUNT_BALANCES,
    });
  },

  /**
   * Transactions
   * GET /api/psd2/transactions/:accountId/year/:year/month/:month
   * GET /api/psd2/transactions/:accountId?pageNo=&pageSize=
   */
  getAccountTransactionsByMonth: (accountId, { year, month }) => {
    return apiClient.get(
      `/psd2/transactions/${accountId}/year/${year}/month/${month}`,
      { apiCallId: apiCallIds.GET_ACCOUNT_TX_MONTH }
    );
  },

  getAllAccountTransactions: (accountId, { pageNo, pageSize } = {}) => {
    const qs = createQueryString(removeFalsyProperties({ pageNo, pageSize }));
    return apiClient.get(
      `/psd2/transactions/${accountId}${qs ? `?${qs}` : ''}`,
      { apiCallId: apiCallIds.GET_ACCOUNT_TX_ALL }
    );
  },

  /**
   * Categories
   * GET /api/psd2/categories
   * POST /api/psd2/categories
   * PUT /api/psd2/categories/:id
   * DELETE /api/psd2/categories/:id
   */
  fetchCategories: (options = {}) => {
    const qs = createQueryString(removeFalsyProperties(options)); // supports search, pageNo, pageSize (optional)
    return apiClient.get(`/psd2/categories${qs ? `?${qs}` : ''}`, {
      apiCallId: apiCallIds.FETCH_CATEGORIES,
    });
  },

  createCategory: ({ name, description }) => {
    const body = removeFalsyProperties({ name, description });
    return apiClient.post(`/psd2/categories`, body, {
      apiCallId: apiCallIds.CREATE_CATEGORY,
    });
  },

  updateCategory: (id, { name, description }) => {
    const body = removeFalsyProperties({ name, description });
    return apiClient.put(`/psd2/categories/${id}`, body, {
      apiCallId: apiCallIds.UPDATE_CATEGORY,
    });
  },

  deleteCategory: (id) => {
    return apiClient.delete(`/psd2/categories/${id}`, {
      apiCallId: apiCallIds.DELETE_CATEGORY,
    });
  },

  /**
   * Aggregation criteria
   * GET /api/psd2/aggregation-criteria
   * POST /api/psd2/aggregation-criteria
   * PUT /api/psd2/aggregation-criteria/:id
   * DELETE /api/psd2/aggregation-criteria/:id
   */
  fetchAggregationCriteria: (options = {}) => {
    const qs = createQueryString(removeFalsyProperties(options)); // supports search, pageNo, pageSize (optional)
    return apiClient.get(`/psd2/aggregation-criteria${qs ? `?${qs}` : ''}`, {
      apiCallId: apiCallIds.FETCH_AGGREGATION_CRITERIA,
    });
  },

  createAggregationCriteria: ({ provider, iban, searchString, categoryId }) => {
    const body = removeFalsyProperties({ provider, iban, searchString, categoryId });
    return apiClient.post(`/psd2/aggregation-criteria`, body, {
      apiCallId: apiCallIds.CREATE_AGGREGATION_CRITERIA,
    });
  },

  updateAggregationCriteria: (id, { provider, iban, searchString, categoryId }) => {
    const body = removeFalsyProperties({ provider, iban, searchString, categoryId });
    return apiClient.put(`/psd2/aggregation-criteria/${id}`, body, {
      apiCallId: apiCallIds.UPDATE_AGGREGATION_CRITERIA,
    });
  },

  deleteAggregationCriteria: (id) => {
    return apiClient.delete(`/psd2/aggregation-criteria/${id}`, {
      apiCallId: apiCallIds.DELETE_AGGREGATION_CRITERIA,
    });
  },

  /**
   * Imported accounts for a loan
   * GET /api/psd2/loan/:loanId/imported-accounts
   */
  getLoanImportedAccounts: (loanId) => {
    return apiClient.get(`/psd2/loan/${loanId}/imported-accounts`, {
      apiCallId: apiCallIds.GET_LOAN_IMPORTED_ACCOUNTS,
    });
  },

  /**
   * Purge PSD2 data for given requisitions
   * POST /api/psd2/requisitions/purge
   * body: { requisitionIds: string[] }
   */
  purgeAccountData: ({ requisitionIds }) => {
    return apiClient.post(
      `/psd2/requisitions/purge`,
      { requisitionIds },
      { apiCallId: apiCallIds.PURGE_ACCOUNT_DATA }
    );
  },
};



// WEBPACK FOOTER //
// ./src/features/psd2/api/index.js